import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Accessibility, X, Type, Eye, MousePointer, Pause, Link2, 
  ZoomIn, ZoomOut, RotateCcw, Moon, Underline, AlignLeft,
  Contrast
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDisplaySettings } from '@/hooks/useDisplaySettings';

interface A11ySettings {
  fontSize: number; // 0 = normal, 1-3 = larger
  highContrast: boolean;
  dyslexiaFont: boolean;
  highlightLinks: boolean;
  pauseAnimations: boolean;
  largeCursor: boolean;
  textSpacing: boolean;
  saturation: number; // 0 = normal, 1 = low, 2 = grayscale
  darkOverlay: boolean;
}

const defaultSettings: A11ySettings = {
  fontSize: 0,
  highContrast: false,
  dyslexiaFont: false,
  highlightLinks: false,
  pauseAnimations: false,
  largeCursor: false,
  textSpacing: false,
  saturation: 0,
  darkOverlay: false,
};

const STORAGE_KEY = 'a11y-settings';

export const AccessibilityWidget: React.FC = () => {
  const { settings: displaySettings, isLoading } = useDisplaySettings();
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<A11ySettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? { ...defaultSettings, ...JSON.parse(stored) } : defaultSettings;
    } catch {
      return defaultSettings;
    }
  });

  const applySettings = useCallback((s: A11ySettings) => {
    const root = document.documentElement;
    const body = document.body;

    // Font size
    const sizeMap = [100, 112, 125, 150];
    root.style.fontSize = `${sizeMap[s.fontSize]}%`;

    // High contrast
    root.classList.toggle('a11y-high-contrast', s.highContrast);

    // Dyslexia font
    root.classList.toggle('a11y-dyslexia-font', s.dyslexiaFont);

    // Highlight links
    root.classList.toggle('a11y-highlight-links', s.highlightLinks);

    // Pause animations
    root.classList.toggle('a11y-pause-animations', s.pauseAnimations);

    // Large cursor
    root.classList.toggle('a11y-large-cursor', s.largeCursor);

    // Text spacing
    root.classList.toggle('a11y-text-spacing', s.textSpacing);

    // Saturation
    root.classList.remove('a11y-low-saturation', 'a11y-grayscale');
    if (s.saturation === 1) root.classList.add('a11y-low-saturation');
    if (s.saturation === 2) root.classList.add('a11y-grayscale');

    // Dark overlay
    root.classList.toggle('a11y-dark-overlay', s.darkOverlay);
  }, []);

  useEffect(() => {
    applySettings(settings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings, applySettings]);

  const update = (key: keyof A11ySettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const reset = () => {
    setSettings(defaultSettings);
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(defaultSettings);

  // Don't render if disabled in admin settings
  if (!displaySettings.show_accessibility_widget) return null;

  const controls: { icon: React.ElementType; label: string; action: () => void; active: boolean }[] = [
    {
      icon: ZoomIn,
      label: `Text Size ${settings.fontSize > 0 ? `+${settings.fontSize}` : ''}`,
      action: () => update('fontSize', (settings.fontSize + 1) % 4),
      active: settings.fontSize > 0,
    },
    {
      icon: Contrast,
      label: 'High Contrast',
      action: () => update('highContrast', !settings.highContrast),
      active: settings.highContrast,
    },
    {
      icon: Type,
      label: 'Dyslexia Font',
      action: () => update('dyslexiaFont', !settings.dyslexiaFont),
      active: settings.dyslexiaFont,
    },
    {
      icon: Link2,
      label: 'Highlight Links',
      action: () => update('highlightLinks', !settings.highlightLinks),
      active: settings.highlightLinks,
    },
    {
      icon: Pause,
      label: 'Pause Animations',
      action: () => update('pauseAnimations', !settings.pauseAnimations),
      active: settings.pauseAnimations,
    },
    {
      icon: MousePointer,
      label: 'Large Cursor',
      action: () => update('largeCursor', !settings.largeCursor),
      active: settings.largeCursor,
    },
    {
      icon: AlignLeft,
      label: 'Text Spacing',
      action: () => update('textSpacing', !settings.textSpacing),
      active: settings.textSpacing,
    },
    {
      icon: Eye,
      label: settings.saturation === 0 ? 'Saturation' : settings.saturation === 1 ? 'Low Saturation' : 'Grayscale',
      action: () => update('saturation', (settings.saturation + 1) % 3),
      active: settings.saturation > 0,
    },
    {
      icon: Moon,
      label: 'Dark Overlay',
      action: () => update('darkOverlay', !settings.darkOverlay),
      active: settings.darkOverlay,
    },
  ];

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'fixed z-[9999] bottom-4 left-4 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-200',
          'bg-black text-white hover:scale-110',
          hasChanges && 'ring-2 ring-black/50 ring-offset-2 ring-offset-white'
        )}
        style={{ backgroundColor: '#000000', color: '#ffffff' }}
        aria-label="Accessibility options"
        title="Accessibility options"
      >
        <Accessibility className="h-6 w-6" />
      </button>

      {/* Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] bg-black/40"
              onClick={() => setIsOpen(false)}
            />

            {/* Widget panel */}
            <motion.div
              initial={{ opacity: 0, x: -300 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -300 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-0 top-0 bottom-0 z-[10000] w-[320px] shadow-2xl overflow-y-auto"
              style={{ backgroundColor: '#ffffff', borderRight: '1px solid #e5e5e5' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 sticky top-0 z-10"
                style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e5e5e5' }}
              >
                <div className="flex items-center gap-2">
                  <Accessibility className="h-5 w-5" style={{ color: '#000000' }} />
                  <h2 className="font-semibold text-sm" style={{ color: '#000000' }}>Accessibility</h2>
                </div>
                <div className="flex items-center gap-1">
                  {hasChanges && (
                    <button
                      onClick={reset}
                      className="flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors"
                      style={{ color: '#555555', backgroundColor: 'transparent' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      title="Reset all settings"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Reset
                    </button>
                  )}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1.5 rounded-md transition-colors"
                    style={{ color: '#000000' }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    aria-label="Close accessibility panel"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Controls grid */}
              <div className="p-4">
                <div className="grid grid-cols-3 gap-2">
                  {controls.map((ctrl) => {
                    const Icon = ctrl.icon;
                    return (
                      <button
                        key={ctrl.label}
                        onClick={ctrl.action}
                        className="flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all duration-150 text-center"
                        style={{
                          backgroundColor: ctrl.active ? '#000000' : '#f5f5f5',
                          borderColor: ctrl.active ? '#000000' : '#e0e0e0',
                          color: ctrl.active ? '#ffffff' : '#000000',
                        }}
                      >
                        <Icon className="h-5 w-5" />
                        <span className="text-[10px] font-medium leading-tight">{ctrl.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Statement */}
                <div className="mt-6 p-3 rounded-lg" style={{ backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                  <p className="text-[11px] leading-relaxed" style={{ color: '#555555' }}>
                    We are committed to ensuring digital accessibility for people with disabilities.
                    We are continually improving the user experience and applying relevant accessibility standards.
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
